import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DriveMetricsResult } from "../../api/observeApi";
import {
  fetchDriveMetrics,
  fetchDriveOutliers,
} from "./deploymentMetricsThunks";
import { RootState } from "app/store";

// Values stored in seconds
const ONE_HOUR = 60 * 60;
const FIFTEEN_MINUTES = 15 * 60;
export const timeIntervals = [
  { value: FIFTEEN_MINUTES, label: "Last 15 Minutes" },
  { value: ONE_HOUR, label: "Last Hour" },
  { value: ONE_HOUR * 6, label: "Last 6 Hours" },
  { value: ONE_HOUR * 12, label: "Last 12 Hours" },
  { value: ONE_HOUR * 18, label: "Last 18 Hours" },
  { value: ONE_HOUR * 24, label: "Last 24 Hours" },
  { value: ONE_HOUR * 24 * 3, label: "Last 3 Days" },
  { value: ONE_HOUR * 24 * 5, label: "Last 5 Days" },
  { value: ONE_HOUR * 24 * 7, label: "Last 7 Days" },
];

export interface DeploymentMetricsState {
  loading: boolean; // global loading for Metrics Page
  reload: boolean;
  loadingDriveMetrics: boolean;
  loadingDriveOutliers: boolean;
  timeInterval: number;
  metricsResults: DriveMetricsResult[];
  outlierResults: string[];
}

const initialState: DeploymentMetricsState = {
  loading: false,
  reload: false,
  loadingDriveMetrics: false,
  loadingDriveOutliers: false,
  timeInterval: ONE_HOUR,
  metricsResults: [],
  outlierResults: [],
};

export const deploymentMetricsSlice = createSlice({
  name: "deploymentMetrics",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setReload: (state, action: PayloadAction<boolean>) => {
      state.reload = action.payload;
    },
    setTimeInterval: (state, action: PayloadAction<number>) => {
      state.timeInterval = action.payload;
    },
    resetDeploymentMetricsState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDriveMetrics.fulfilled, (state, action) => {
        state.loadingDriveMetrics = false;
        if (action.payload) {
          state.metricsResults = action.payload;
        } else {
          state.metricsResults = [];
        }
      })
      .addCase(fetchDriveMetrics.pending, (state) => {
        state.loadingDriveMetrics = true;
      })
      .addCase(fetchDriveMetrics.rejected, (state, action) => {
        state.loadingDriveMetrics = false;
        state.metricsResults = [];
        console.log("failed", action);
      })
      .addCase(fetchDriveOutliers.fulfilled, (state, action) => {
        state.loadingDriveOutliers = false;
        state.outlierResults = action.payload;
      })
      .addCase(fetchDriveOutliers.pending, (state) => {
        state.loadingDriveOutliers = true;
      })
      .addCase(fetchDriveOutliers.rejected, (state, action) => {
        state.loadingDriveOutliers = false;
        state.outlierResults = [];
        console.log("failed", action);
      });
  },
});

export const {
  setLoading,
  setReload,
  setTimeInterval,
  resetDeploymentMetricsState,
} = deploymentMetricsSlice.actions;

export const loading = (state: RootState) => state.deploymentMetrics.loading;
export const timeInterval = (state: RootState) =>
  state.deploymentMetrics.timeInterval;
export const metricsResults = (state: RootState) =>
  state.deploymentMetrics.metricsResults;
export const outlierResults = (state: RootState) =>
  state.deploymentMetrics.outlierResults;

export default deploymentMetricsSlice.reducer;
